import type { SupportOverTimeResponse } from '../shared/api';
import { api, escapeHtml } from './http';
import { pageLayout } from './layout';

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 500;
const PLOT = { left: 76, right: 28, top: 28, bottom: 66 };

export async function renderSupportOverTimePage(root: HTMLElement): Promise<void> {
  root.innerHTML = pageLayout('Support Over Time', '<div id="supportOverTime"></div>', { publicNav: true, wide: true, showAbout: false });
  const content = root.querySelector<HTMLElement>('#supportOverTime')!;
  const load = async (weighting: SupportOverTimeResponse['weighting'], includeBias: boolean) => {
    content.innerHTML = '<div class="loading-state"><span class="spinner"></span>Loading support history…</div>';
    try {
      const query = new URLSearchParams({ weighting, includeBias: String(includeBias) });
      const results = await api<SupportOverTimeResponse>(`/api/support-over-time?${query}`);
      content.innerHTML = renderSupportOverTime(results);
      installSupportTimeChart(content, results);
      content.querySelectorAll<HTMLSelectElement>('.support-time-filter').forEach((select) => {
        select.addEventListener('change', () => {
          const nextWeighting = content.querySelector<HTMLSelectElement>('#supportTimeWeighting')!.value as SupportOverTimeResponse['weighting'];
          const nextIncludeBias = content.querySelector<HTMLSelectElement>('#supportTimeSurveyScope')!.value === 'all';
          void load(nextWeighting, nextIncludeBias);
        });
      });
    } catch (error) {
      content.innerHTML = `<div class="error-state">${escapeHtml(error instanceof Error ? error.message : 'Support history could not be loaded.')}</div>`;
    }
  };
  await load('raw', true);
}

export function renderSupportOverTime(results: SupportOverTimeResponse): string {
  const lastPoint = results.points.at(-1);
  return `<section class="card support-time-intro">
      <div>
        <p class="eyebrow">Q13 · Q6 on the Shortened Survey</p>
        <h2>Disc golf course support over time</h2>
        <p>This chart combines “Strongly Support” with “Support,” and “Do Not Support” with “Strongly Do Not Support.” Percentages are cumulative among decided responses; “Not Sure” is excluded.</p>
      </div>
      <div class="support-time-side">
        <div class="support-time-filters">
          <label>Weighting<select id="supportTimeWeighting" class="support-time-filter">
            <option value="raw"${results.weighting === 'raw' ? ' selected' : ''}>Raw respondents</option>
            <option value="weighted"${results.weighting === 'weighted' ? ' selected' : ''}>Household weighted</option>
          </select></label>
          <label>Survey data<select id="supportTimeSurveyScope" class="support-time-filter">
            <option value="all"${results.surveyScope === 'all' ? ' selected' : ''}>Include bias surveys</option>
            <option value="main"${results.surveyScope === 'main' ? ' selected' : ''}>Main Survey only</option>
          </select></label>
        </div>
        <div class="support-time-summary">
          <span><strong>${lastPoint ? formatCount(lastPoint.decidedCount) : '0'}</strong> decided ${results.weighting === 'weighted' ? 'households' : 'responses'}</span>
          <span><strong>${results.excludedNotSureCount}</strong> not sure excluded</span>
        </div>
      </div>
    </section>
    ${renderSupportTimeChartCard(results)}`;
}

export function renderSupportTimeChartCard(results: SupportOverTimeResponse, options: { embedded?: boolean } = {}): string {
  const lastPoint = results.points.at(-1);
  return lastPoint ? `<section class="card support-time-chart-card${options.embedded ? ' embedded-support-time-card' : ''}">
      ${options.embedded ? `<div class="support-time-embedded-heading">
        <div><p class="eyebrow">Q13 · Q6 on Shortened Survey</p><h2>Support over time</h2><p>All three variants · Raw respondents</p></div>
        <a href="/support-over-time">Open full chart</a>
      </div>` : ''}
      <div class="support-time-chart-controls">
        <div class="support-time-legend" aria-label="Chart legend">
          <span><i class="support-line-key"></i>Support <strong>${formatPercent(lastPoint.supportPercent)}</strong></span>
          <span><i class="oppose-line-key"></i>Do not support <strong>${formatPercent(lastPoint.doNotSupportPercent)}</strong></span>
        </div>
        <button id="resetSupportTimeView" class="secondary-button" type="button" disabled>Reset view</button>
      </div>
      <p id="supportTimeRangeLabel" class="support-time-range-label">Showing all ${results.decidedResponseCount} decided responses</p>
      <div id="supportTimeChartMount">${renderChart(results, { start: 0, end: results.points.length - 1 })}</div>
      <p class="support-time-caption">Hover to inspect a response. Drag horizontally across the graph to zoom into a time range. Each point adds one decided response in submission order; counts and percentages remain ${results.weighting === 'weighted' ? 'household-weighted and ' : ''}cumulative from the beginning of the selected survey data.</p>
    </section>` : `<section class="card support-time-empty${options.embedded ? ' embedded-support-time-card' : ''}"><h2>Support over time</h2><p>The graph will appear after the first support or do-not-support answer is submitted.</p></section>`;
}

interface ChartRange {
  start: number;
  end: number;
}

function renderChart(results: SupportOverTimeResponse, range: ChartRange): string {
  const points = results.points.slice(range.start, range.end + 1);
  const plotWidth = CHART_WIDTH - PLOT.left - PLOT.right;
  const plotHeight = CHART_HEIGHT - PLOT.top - PLOT.bottom;
  const firstTime = new Date(points[0]!.submittedAt).getTime();
  const lastTime = new Date(points.at(-1)!.submittedAt).getTime();
  const duration = lastTime - firstTime;
  const x = (submittedAt: string, index: number) => duration === 0
    ? PLOT.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth)
    : PLOT.left + ((new Date(submittedAt).getTime() - firstTime) / duration) * plotWidth;
  const y = (percent: number) => PLOT.top + (1 - percent) * plotHeight;
  const path = (field: 'supportPercent' | 'doNotSupportPercent') => points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(point.submittedAt, index).toFixed(2)} ${y(point[field]).toFixed(2)}`)
    .join(' ');
  const yTicks = [0, .25, .5, .75, 1];
  const dateLabels = duration === 0
    ? [{ x: PLOT.left + plotWidth / 2, anchor: 'middle', label: formatAxisTime(points[0]!.submittedAt) }]
    : [
        { x: PLOT.left, anchor: 'start', label: formatAxisTime(points[0]!.submittedAt) },
        { x: PLOT.left + plotWidth / 2, anchor: 'middle', label: formatAxisTime(new Date(firstTime + duration / 2).toISOString()) },
        { x: PLOT.left + plotWidth, anchor: 'end', label: formatAxisTime(points.at(-1)!.submittedAt) },
      ];
  return `<div class="support-time-chart-wrap"><div class="support-time-chart-scroll"><svg class="support-time-chart" viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" role="img" aria-labelledby="supportChartTitle supportChartDescription">
    <title id="supportChartTitle">${results.weighting === 'weighted' ? 'Household-weighted cumulative' : 'Cumulative'} support and do-not-support percentages over time</title>
    <desc id="supportChartDescription">${points.length} decided responses from ${formatDateTime(points[0]!.submittedAt)} through ${formatDateTime(points.at(-1)!.submittedAt)}. The final result is ${formatPercent(points.at(-1)!.supportPercent)} support and ${formatPercent(points.at(-1)!.doNotSupportPercent)} do not support.</desc>
    ${yTicks.map((tick) => `<line class="support-chart-grid" x1="${PLOT.left}" y1="${y(tick)}" x2="${PLOT.left + plotWidth}" y2="${y(tick)}"></line><text class="support-chart-axis-label" x="${PLOT.left - 14}" y="${y(tick) + 5}" text-anchor="end">${tick * 100}%</text>`).join('')}
    <line class="support-chart-axis" x1="${PLOT.left}" y1="${PLOT.top}" x2="${PLOT.left}" y2="${PLOT.top + plotHeight}"></line>
    <line class="support-chart-axis" x1="${PLOT.left}" y1="${PLOT.top + plotHeight}" x2="${PLOT.left + plotWidth}" y2="${PLOT.top + plotHeight}"></line>
    ${dateLabels.map((tick) => `<text class="support-chart-axis-label" x="${tick.x}" y="${CHART_HEIGHT - 28}" text-anchor="${tick.anchor}">${tick.label}</text>`).join('')}
    <path class="support-chart-line support-chart-line-support" d="${path('supportPercent')}"></path>
    <path class="support-chart-line support-chart-line-oppose" d="${path('doNotSupportPercent')}"></path>
    ${renderLastPoint(points, x, y, 'supportPercent', 'support-chart-point-support')}
    ${renderLastPoint(points, x, y, 'doNotSupportPercent', 'support-chart-point-oppose')}
    <g class="support-chart-hover" hidden>
      <line class="support-chart-hover-line" y1="${PLOT.top}" y2="${PLOT.top + plotHeight}"></line>
      <circle class="support-chart-hover-support" r="7"></circle>
      <circle class="support-chart-hover-oppose" r="7"></circle>
    </g>
    <rect class="support-chart-selection" y="${PLOT.top}" height="${plotHeight}" hidden></rect>
    <rect class="support-chart-overlay" x="${PLOT.left}" y="${PLOT.top}" width="${plotWidth}" height="${plotHeight}"></rect>
  </svg></div><div class="support-chart-tooltip" role="status" hidden></div></div>`;
}

export function installSupportTimeChart(content: HTMLElement, results: SupportOverTimeResponse): void {
  if (results.points.length === 0) return;
  const mount = content.querySelector<HTMLElement>('#supportTimeChartMount');
  const resetButton = content.querySelector<HTMLButtonElement>('#resetSupportTimeView');
  const rangeLabel = content.querySelector<HTMLElement>('#supportTimeRangeLabel');
  if (!mount || !resetButton || !rangeLabel) return;
  const fullRange = { start: 0, end: results.points.length - 1 };
  let range = { ...fullRange };

  const update = () => {
    mount.innerHTML = renderChart(results, range);
    const showingAll = range.start === fullRange.start && range.end === fullRange.end;
    resetButton.disabled = showingAll;
    rangeLabel.textContent = showingAll
      ? `Showing all ${results.decidedResponseCount} decided responses`
      : `Showing responses ${range.start + 1}–${range.end + 1} · ${formatDateTime(results.points[range.start]!.submittedAt)} to ${formatDateTime(results.points[range.end]!.submittedAt)}`;
    installInteractions();
  };

  const installInteractions = () => {
    const svg = mount.querySelector<SVGSVGElement>('.support-time-chart')!;
    const overlay = svg.querySelector<SVGRectElement>('.support-chart-overlay')!;
    const selection = svg.querySelector<SVGRectElement>('.support-chart-selection')!;
    const hover = svg.querySelector<SVGGElement>('.support-chart-hover')!;
    const hoverLine = hover.querySelector<SVGLineElement>('.support-chart-hover-line')!;
    const hoverSupport = hover.querySelector<SVGCircleElement>('.support-chart-hover-support')!;
    const hoverOppose = hover.querySelector<SVGCircleElement>('.support-chart-hover-oppose')!;
    const chartWrap = mount.querySelector<HTMLElement>('.support-time-chart-wrap')!;
    const tooltip = chartWrap.querySelector<HTMLElement>('.support-chart-tooltip')!;
    const visiblePoints = results.points.slice(range.start, range.end + 1);
    const x = createXScale(visiblePoints);
    const y = (percent: number) => PLOT.top + (1 - percent) * (CHART_HEIGHT - PLOT.top - PLOT.bottom);
    let dragStart: number | null = null;

    const chartX = (event: PointerEvent) => {
      const bounds = svg.getBoundingClientRect();
      return clamp(((event.clientX - bounds.left) / bounds.width) * CHART_WIDTH, PLOT.left, CHART_WIDTH - PLOT.right);
    };
    const nearestVisibleIndex = (targetX: number) => {
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      visiblePoints.forEach((point, index) => {
        const nextDistance = Math.abs(x(point.submittedAt, index) - targetX);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    };
    const hideHover = () => {
      hover.setAttribute('hidden', '');
      tooltip.hidden = true;
    };
    const showHover = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const index = nearestVisibleIndex(chartX(event));
      const point = visiblePoints[index]!;
      const pointX = x(point.submittedAt, index);
      hover.removeAttribute('hidden');
      hoverLine.setAttribute('x1', String(pointX));
      hoverLine.setAttribute('x2', String(pointX));
      hoverSupport.setAttribute('cx', String(pointX));
      hoverSupport.setAttribute('cy', String(y(point.supportPercent)));
      hoverOppose.setAttribute('cx', String(pointX));
      hoverOppose.setAttribute('cy', String(y(point.doNotSupportPercent)));
      tooltip.innerHTML = `<strong>${formatDateTime(point.submittedAt)}</strong><span>${formatCount(point.decidedCount)} decided ${results.weighting === 'weighted' ? 'households' : 'responses'}</span><span class="support-tooltip-support">Support: <b>${formatPercent(point.supportPercent)}</b> (${formatCount(point.supportCount)})</span><span class="support-tooltip-oppose">Do not support: <b>${formatPercent(point.doNotSupportPercent)}</b> (${formatCount(point.doNotSupportCount)})</span>`;
      tooltip.hidden = false;
      const wrapBounds = chartWrap.getBoundingClientRect();
      const proposedLeft = event.clientX - wrapBounds.left + 14;
      const maxLeft = Math.max(8, chartWrap.clientWidth - tooltip.offsetWidth - 8);
      tooltip.style.left = `${clamp(proposedLeft, 8, maxLeft)}px`;
      const above = event.clientY - wrapBounds.top - tooltip.offsetHeight - 14;
      tooltip.style.top = `${above >= 8 ? above : event.clientY - wrapBounds.top + 14}px`;
    };

    overlay.addEventListener('pointermove', (event) => {
      if (dragStart === null) {
        showHover(event);
        return;
      }
      const current = chartX(event);
      selection.removeAttribute('hidden');
      selection.setAttribute('x', String(Math.min(dragStart, current)));
      selection.setAttribute('width', String(Math.abs(current - dragStart)));
    });
    overlay.addEventListener('pointerleave', () => {
      if (dragStart === null) hideHover();
    });
    overlay.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || visiblePoints.length < 2) return;
      dragStart = chartX(event);
      hideHover();
      overlay.setPointerCapture(event.pointerId);
    });
    overlay.addEventListener('pointerup', (event) => {
      if (dragStart === null) return;
      const dragEnd = chartX(event);
      overlay.releasePointerCapture(event.pointerId);
      selection.setAttribute('hidden', '');
      if (Math.abs(dragEnd - dragStart) >= 8) {
        const first = nearestVisibleIndex(Math.min(dragStart, dragEnd));
        const last = nearestVisibleIndex(Math.max(dragStart, dragEnd));
        if (first !== last) {
          range = { start: range.start + first, end: range.start + last };
          dragStart = null;
          update();
          return;
        }
      }
      dragStart = null;
      showHover(event);
    });
    overlay.addEventListener('pointercancel', () => {
      dragStart = null;
      selection.setAttribute('hidden', '');
      hideHover();
    });
  };

  resetButton.addEventListener('click', () => {
    range = { ...fullRange };
    update();
  });
  update();
}

function createXScale(points: SupportOverTimeResponse['points']) {
  const plotWidth = CHART_WIDTH - PLOT.left - PLOT.right;
  const firstTime = new Date(points[0]!.submittedAt).getTime();
  const lastTime = new Date(points.at(-1)!.submittedAt).getTime();
  const duration = lastTime - firstTime;
  return (submittedAt: string, index: number) => duration === 0
    ? PLOT.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth)
    : PLOT.left + ((new Date(submittedAt).getTime() - firstTime) / duration) * plotWidth;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(value, maximum));
}

function renderLastPoint(
  points: SupportOverTimeResponse['points'],
  x: (submittedAt: string, index: number) => number,
  y: (percent: number) => number,
  field: 'supportPercent' | 'doNotSupportPercent',
  className: string,
): string {
  const index = points.length - 1;
  const point = points[index]!;
  return `<circle class="${className}" cx="${x(point.submittedAt, index)}" cy="${y(point[field])}" r="6"><title>${formatPercent(point[field])} after ${point.decidedCount} decided responses</title></circle>`;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function formatCount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function formatAxisTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString();
}
